/**
 * Import Hospital Representantes Modal
 *
 * Epic 28: Upload de representantes por hospital específico
 * Endpoint: /api/hospitais/{id}/representantes/import-excel
 */
import React, { useState, useRef } from 'react';
import { X, Upload, Download, CheckCircle, AlertCircle, AlertTriangle } from 'lucide-react';
import hospitalRepresentantesService, { ImportRepresentantesResult } from '../services/hospitalRepresentantesService';
import { msg } from '../utils/messages';

interface ImportHospitalRepresentantesModalProps {
  hospitalId: number;
  hospitalNome: string;
  onClose: () => void;
  onSuccess: () => void;
}

const ImportHospitalRepresentantesModal: React.FC<ImportHospitalRepresentantesModalProps> = ({
  hospitalId,
  hospitalNome,
  onClose,
  onSuccess
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ImportRepresentantesResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setResult(null);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    try {
      setLoading(true);
      const importResult = await hospitalRepresentantesService.importExcel(hospitalId, file);
      setResult(importResult);

      if (importResult.errors.length === 0) {
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Erro ao importar arquivo';
      msg.error(errorMessage);
      console.error('Import error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await hospitalRepresentantesService.downloadTemplate(hospitalId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `template_representantes_hospital_${hospitalId}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      msg.error('Erro ao baixar template');
      console.error('Template download error:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <Upload className="w-6 h-6 text-blue-600" />
              Importar Representantes (Excel)
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Hospital: <span className="font-semibold">{hospitalNome}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Download Template */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800 mb-2">
              📥 <strong>Passo 1:</strong> Baixe o template Excel pré-preenchido com dados do hospital:
            </p>
            <button
              onClick={handleDownloadTemplate}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Download className="w-4 h-4" />
              Baixar Template (.xlsx)
            </button>
            <p className="text-xs text-blue-700 mt-2">
              💡 O template inclui o código e nome do hospital automaticamente
            </p>
          </div>

          {/* Instructions */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-800 mb-2 text-sm">📋 Instruções:</h3>
            <ul className="text-xs text-gray-700 space-y-1 list-disc list-inside">
              <li>Preencha as colunas: <strong>Codigo, nome_representante, e-mail, telefone</strong></li>
              <li>O <strong>código_hospital</strong> deve corresponder ao hospital atual</li>
              <li>Codigo = CPF/CNPJ do representante (único)</li>
              <li>Os representantes serão vinculados automaticamente a este hospital</li>
            </ul>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <strong>Passo 2:</strong> Selecione o arquivo Excel preenchido
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
            />
            {file && (
              <p className="mt-2 text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                Arquivo selecionado: <strong>{file.name}</strong>
              </p>
            )}
          </div>

          {/* Import Button */}
          <button
            onClick={handleImport}
            disabled={!file || loading}
            className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                Importando...
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                Importar Representantes
              </>
            )}
          </button>

          {/* Result */}
          {result && (
            <div className="space-y-3 mt-4">
              {/* Success Stats */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Importação Concluída
                </h3>
                <div className="text-sm text-green-700 space-y-1">
                  <p>✅ <strong>{result.created}</strong> representantes criados</p>
                  <p>🔄 <strong>{result.updated}</strong> representantes atualizados</p>
                  <p>🔗 <strong>{result.vinculos_criados}</strong> vínculos criados com o hospital</p>
                  <p>📊 <strong>{result.total_linhas_processadas}</strong> linhas processadas</p>
                </div>
              </div>

              {/* Warnings */}
              {result.warnings.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h3 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Avisos ({result.warnings.length})
                  </h3>
                  <ul className="text-sm text-yellow-700 space-y-1 max-h-32 overflow-y-auto">
                    {result.warnings.map((warning, idx) => (
                      <li key={idx}>⚠️ {warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Errors */}
              {result.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Erros ({result.errors.length})
                  </h3>
                  <ul className="text-sm text-red-700 space-y-1 max-h-32 overflow-y-auto">
                    {result.errors.map((error, idx) => (
                      <li key={idx}>❌ {error}</li>
                    ))}
                  </ul>
                  <p className="text-xs text-red-600 mt-2">
                    💡 Corrija os erros no Excel e importe novamente
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {result && result.errors.length === 0 ? 'Concluir' : 'Cancelar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportHospitalRepresentantesModal;
